import React from "react";
import Str from "../../../../common/Str";
import Checkbox from "../../../../library/Checkbox";
import Pagination from "../../../../library/Pagination";
import Button from "../../../../library/Button";
import {
    ActionsCell,
    LinkedProgrammesCountCell,
    NetsuiteProductCell,
    PortalNameCell,
    PreRequisitesCell,
    SkeletonRow,
    StudyPathCell,
} from "./VersionModulesRowCells";
import {
    StyledActionsHeadCell,
    StyledCheckboxCell,
    StyledCheckboxHeadCell,
    StyledCodeCell,
    StyledCodeHeadCell,
    StyledCountHeadCell,
    StyledCreditsCell,
    StyledCreditsHeadCell,
    StyledErrorRow,
    StyledLoadingRow,
    StyledNetsuiteHeadCell,
    StyledPortalHeadCell,
    StyledPrereqHeadCell,
    StyledSortButton,
    StyledSortIcon,
    StyledStudyPathHeadCell,
    StyledTable,
    StyledTableScroller,
    StyledTermCell,
    StyledTermHeadCell,
    StyledYearCell,
    StyledYearHeadCell,
} from "./VersionModulesTable.styles";
import { TABLE_ROWS_PER_PAGE } from "../../../../../utils/Constants";

/**
 * Version Modules table (SPEC3 R1): the version's linked modules with study-path
 * placement, row selection for the bulk unlink bar, sorting and pagination.
 * Visual values (header height, cell padding, zebra striping, pagination) match
 * the Career Library careers table's system styling; row-selection/bulk-action
 * wiring mirrors the sibling `linkedProgrammes/LinkedProgrammesTable.js`.
 * @param {object} hook State and handlers from `useVersionModules`.
 */
function VersionModulesTable({ hook }) {
    const isPaginationDisabled = hook.loading || hook.isError || hook.isProcessing;

    const paginationDisabledReason = hook.isProcessing
        ? "A bulk action is currently running"
        : hook.loading
            ? "Modules are loading"
            : hook.isError
                ? "Modules couldn't be loaded"
                : undefined;

    const renderSortHeader = (label, field) => (
        <StyledSortButton type="button" onClick={() => hook.changeSort(field)} disabled={hook.isProcessing}>
            {label}
            <StyledSortIcon className="fal fa-angles-up-down" aria-hidden="true" />
        </StyledSortButton>
    );

    return (
        <>
            <StyledTableScroller>
                <StyledTable>
                    <thead>
                        <tr>
                            <StyledCheckboxHeadCell>
                                <Checkbox
                                    checked={hook.allMatchingSelected}
                                    indeterminate={hook.someMatchingSelected}
                                    disabled={hook.isProcessing || hook.loading || hook.totalRows === 0 || hook.isLocked}
                                    onChange={hook.toggleAllMatching}
                                    ariaLabel="Select all modules matching current filters"
                                />
                            </StyledCheckboxHeadCell>

                            <StyledPortalHeadCell>{renderSortHeader("Portal Name", "Portal")}</StyledPortalHeadCell>
                            <StyledCodeHeadCell>{renderSortHeader("Code", "Code")}</StyledCodeHeadCell>
                            <StyledCreditsHeadCell>{renderSortHeader("Cr", "credits")}</StyledCreditsHeadCell>
                            <StyledStudyPathHeadCell>{renderSortHeader("Study Path", "studyPath")}</StyledStudyPathHeadCell>
                            <StyledTermHeadCell>{renderSortHeader("Term", "term")}</StyledTermHeadCell>
                            <StyledYearHeadCell>{renderSortHeader("Year", "year")}</StyledYearHeadCell>
                            <StyledCountHeadCell><i className="fal fa-books" title="Linked programmes" aria-hidden="true" /></StyledCountHeadCell>
                            <StyledNetsuiteHeadCell>NetSuite Product</StyledNetsuiteHeadCell>
                            <StyledPrereqHeadCell>Pre-Requisites</StyledPrereqHeadCell>
                            <StyledActionsHeadCell>Action</StyledActionsHeadCell>
                        </tr>
                    </thead>

                    <tbody>
                        {hook.loading ? (
                            Array(5).fill(null).map((skeletonPlaceholder, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <tr key={`skeleton-${index}`}>
                                    <SkeletonRow />
                                </tr>
                            ))
                        ) : hook.isError ? (
                            <tr>
                                <StyledErrorRow colSpan={11}>
                                    Modules couldn&apos;t be loaded.
                                    {" "}
                                    <Button type="button" variant="primary" size="sm" onClick={hook.refresh}>Retry</Button>
                                </StyledErrorRow>
                            </tr>
                        ) : hook.rows.length === 0 ? (
                            <tr>
                                <StyledLoadingRow colSpan={11}>
                                    {Str.noRecord}
                                </StyledLoadingRow>
                            </tr>
                        ) : (
                            hook.rows.map((row) => (
                                <tr key={row.Id}>
                                    <StyledCheckboxCell>
                                        <Checkbox
                                            checked={hook.selectedIds.includes(row.Id)}
                                            disabled={hook.isProcessing || hook.isLocked}
                                            onChange={() => hook.toggleRow(row.Id)}
                                            ariaLabel={`Select ${row?.Portal || "module"}`}
                                        />
                                    </StyledCheckboxCell>

                                    <PortalNameCell row={row} />

                                    <StyledCodeCell>{row.Code || "-"}</StyledCodeCell>
                                    <StyledCreditsCell>{row.credits ? `${row.credits}cr` : "-"}</StyledCreditsCell>

                                    <StudyPathCell row={row} />

                                    <StyledTermCell>{row.term ?? "-"}</StyledTermCell>
                                    <StyledYearCell>{row.year ?? "-"}</StyledYearCell>
                                    <LinkedProgrammesCountCell row={row} />

                                    <NetsuiteProductCell
                                        row={row}
                                        netsuiteList={hook.netsuiteList}
                                        isNetsuiteLoading={hook.isNetsuiteLoading}
                                    />

                                    <PreRequisitesCell row={row} moduleNameById={hook.moduleNameById} />

                                    <ActionsCell row={row} hook={hook} />
                                </tr>
                            ))
                        )}
                    </tbody>
                </StyledTable>
            </StyledTableScroller>

            {!hook.loading && !hook.isError && hook.rows.length > 0 && (
                <Pagination
                    totalEntries={hook.totalRows}
                    rangeStart={hook.rangeStart}
                    rangeEnd={hook.rangeEnd}
                    rowsPerPage={hook.perPage}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                    onRowsPerPageChange={(value) => {
                        hook.setPerPage(value);
                        hook.setPage(1);
                    }}
                    currentPage={hook.page}
                    totalPages={hook.totalPages}
                    onPageChange={hook.setPage}
                    disabled={isPaginationDisabled}
                    disabledReason={paginationDisabledReason}
                />
            )}
        </>
    );
}

export default VersionModulesTable;
